import React from 'react';
import { CrossLandingPage } from './types';

interface CrossLandingPageTableProps {
  data: CrossLandingPage[];
  selectedIds: number[];
  onSelectIds: (ids: number[]) => void;
  onEdit: (id: number) => void;
  onDelete: (id: number) => Promise<void>;
  apiBaseUrl: string;
  csrfToken: string;
  onRefresh: () => Promise<void>;
  sortColumn?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (column: string) => void;
}

const CrossLandingPageTable: React.FC<CrossLandingPageTableProps> = ({
  data,
  selectedIds,
  onSelectIds,
  onEdit,
  onDelete,
  apiBaseUrl,
  csrfToken,
  onRefresh,
  sortColumn,
  sortDirection,
  onSort,
}) => {
  // ソートアイコンを取得
  const getSortIcon = (column: string) => {
    if (!onSort) return null;
    if (sortColumn !== column) {
      return <i className="bi bi-arrow-down-up text-muted ms-1" style={{ fontSize: '0.8em' }} />;
    }
    if (sortDirection === 'asc') {
      return <i className="bi bi-arrow-up ms-1" style={{ fontSize: '0.8em' }} />;
    }
    return <i className="bi bi-arrow-down ms-1" style={{ fontSize: '0.8em' }} />;
  };

  // ソート可能なヘッダーをレンダリング
  const renderSortableHeader = (column: string, label: string, style?: React.CSSProperties) => {
    if (!onSort) {
      return <th style={style}>{label}</th>;
    }
    return (
      <th
        style={{ ...style, cursor: 'pointer', userSelect: 'none' }}
        onClick={() => onSort(column)}
        onMouseEnter={(e) => {
          if (sortColumn !== column) {
            e.currentTarget.style.backgroundColor = '#f8f9fa';
          }
        }}
        onMouseLeave={(e) => {
          if (sortColumn !== column) {
            e.currentTarget.style.backgroundColor = '';
          }
        }}
      >
        {label}
        {getSortIcon(column)}
      </th>
    );
  };
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      onSelectIds(data.map((lp) => lp.id!).filter((id): id is number => id !== undefined));
    } else {
      onSelectIds([]);
    }
  };

  const handleSelectOne = (id: number, checked: boolean) => {
    if (checked) {
      onSelectIds([...selectedIds, id]);
    } else {
      onSelectIds(selectedIds.filter((selectedId) => selectedId !== id));
    }
  };

  const handleTogglePublished = async (lp: CrossLandingPage) => {
    try {
      const response = await fetch(`${apiBaseUrl}/${lp.id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'X-CSRF-Token': csrfToken,
        },
        body: JSON.stringify({
          cross_landing_page: {
            published: !lp.published,
          },
        }),
      });

      if (response.ok) {
        await onRefresh();
      } else {
        alert('公開ステータスの更新に失敗しました');
      }
    } catch (error) {
      console.error('Toggle published failed:', error);
      alert('公開ステータスの更新に失敗しました');
    }
  };

  return (
    <div className="table-responsive">
      <table className="table table-hover">
        <thead>
          <tr>
            <th style={{ width: '40px' }}>
              <input
                type="checkbox"
                checked={data.length > 0 && selectedIds.length === data.length}
                onChange={handleSelectAll}
              />
            </th>
            {renderSortableHeader('id', 'ID', { width: '60px' })}
            <th>タイトル</th>
            {renderSortableHeader('primary_type', 'Primary')}
            {renderSortableHeader('secondary_type', 'Secondary')}
            {renderSortableHeader('job_count', '求人数', { width: '100px' })}
            {renderSortableHeader('published', '公開', { width: '80px' })}
            {renderSortableHeader('display_priority', '優先度', { width: '80px' })}
            {renderSortableHeader('created_at', '作成日', { width: '120px' })}
            <th style={{ width: '100px' }}>操作</th>
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={10} className="text-center text-muted">
                データがありません
              </td>
            </tr>
          ) : (
            data.map((lp) => (
              <tr key={lp.id}>
                <td>
                  <input
                    type="checkbox"
                    checked={selectedIds.includes(lp.id!)}
                    onChange={(e) => handleSelectOne(lp.id!, e.target.checked)}
                  />
                </td>
                <td>{lp.id}</td>
                <td>
                  <div className="text-truncate" style={{ maxWidth: '200px' }} title={`${lp.primary_name || lp.primary_id} × ${lp.secondary_name || lp.secondary_id}`}>
                    {lp.published && lp.id ? (
                      <a
                        href={`/job_listings/cross/${lp.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-decoration-none"
                        onClick={(e) => e.stopPropagation()}
                        title="詳細ページを開く（新しいタブ）"
                      >
                        {lp.primary_name || lp.primary_id} × {lp.secondary_name || lp.secondary_id}
                        <i className="bi bi-box-arrow-up-right ms-1" style={{ fontSize: '0.8em' }} />
                      </a>
                    ) : (
                      <span>{lp.primary_name || lp.primary_id} × {lp.secondary_name || lp.secondary_id}</span>
                    )}
                  </div>
                </td>
                <td>
                  <small>
                    {lp.primary_type}: {lp.primary_name || lp.primary_id}
                  </small>
                </td>
                <td>
                  <small>
                    {lp.secondary_type}: {lp.secondary_name || lp.secondary_id}
                  </small>
                </td>
                <td>
                  <span className="badge bg-secondary">
                    {lp.job_count !== undefined ? lp.job_count.toLocaleString() : '-'}件
                  </span>
                </td>
                <td>
                  <div className="form-check form-switch">
                    <input
                      className="form-check-input"
                      type="checkbox"
                      checked={lp.published}
                      onChange={() => handleTogglePublished(lp)}
                    />
                  </div>
                </td>
                <td>{lp.display_priority}</td>
                <td>
                  <small>
                    {lp.created_at
                      ? new Date(lp.created_at).toLocaleDateString('ja-JP')
                      : '-'}
                  </small>
                </td>
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-primary me-1"
                    onClick={() => onEdit(lp.id!)}
                  >
                    編集
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-danger"
                    onClick={() => onDelete(lp.id!)}
                  >
                    削除
                  </button>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

export default CrossLandingPageTable;

